<script>
    import { scaleLinear } from 'd3-scale';
    import { line, curveMonotoneX, curveStep } from 'd3-shape';
    import { axisBottom, axisLeft } from 'd3-axis';
    import { select, selectAll } from 'd3-selection';
    import { tick } from 'svelte';
    import cityNamesStyled from '../data/citie_names_styled.json';
    
    export let xExtent;
    export let yExtent;
    export let data;
    export let showAxisLabels = true;
    export let columns;
    export let i;
    let width;
    let height;
    let chartHeight;
    let chartWidth;
    let containerElement;
    let linePath;
    let trendPath;
    let xAxisElement;
    let yAxisElement;
    $: margin = showAxisLabels 
        ? { top: 15, right: 15, bottom: 24, left: 0 }
        : { top: 15, right: 15, bottom: 24, left: 0 };

    const city = cityNamesStyled[data[0].city] || data[0].city;
    const country = data[0].country;
    const games_year = data[0].games_year;
    const venue = data[0].venue;

    // Force width remeasurement when columns change
    $: if (columns && containerElement) {
        tick().then(() => {
            width = containerElement.clientWidth;
        });
    }

    $:if (width && height) {
        chartWidth = width - margin.left - margin.right;
        chartHeight = height - margin.top - margin.bottom;
        const xScale = scaleLinear()
            .domain(xExtent)
            .range([0, chartWidth]);

        const yScale = scaleLinear()
            .domain(yExtent)
            .range([chartHeight, 0]);

        const lineGenerator = line()
            .x(d => xScale(d.valid_time))
            .y(d => yScale(d.value))
            .curve(curveStep);

        linePath = lineGenerator(data);

        const trendGenerator = line()
            .x(d => xScale(d.valid_time))
            .y(d => yScale(d.trend));

        trendPath = trendGenerator(data);

        if (xAxisElement && yAxisElement) {
            const xAxis = axisBottom(xScale)
                .tickSize(6)
                .tickPadding(6)
                .tickFormat(d => d.toString())
                .tickValues([1950, 2025]);

            const yAxis = axisLeft(yScale)
                .tickSize(-chartWidth)
                .tickPadding(6)
                .tickValues([0, 75, 150]);

            // Call the axis functions on the DOM elements
            select(xAxisElement).call(xAxis);
            select(yAxisElement).call(yAxis);

            selectAll('.multiple-chart[data-venue="'+venue+'"] .x-axis .tick').filter((_, i) => i === 0).classed('first', true);
            selectAll('.multiple-chart[data-venue="'+venue+'"] .x-axis .tick').filter((_, i, arr) => i === arr.length -1).classed('last', true);

            selectAll('.multiple-chart[data-venue="'+venue+'"] .y-axis .tick').filter((_, i, arr) => i === arr.length - 1).classed('last', true);

            selectAll('.multiple-chart[data-venue="'+venue+'"] .y-axis .tick').filter((d) => {
                return d === 0;
            }).classed('zero', true);

            selectAll('.multiple-chart[data-venue="'+venue+'"] .y-axis .tick').filter((d, i, arr) => {
                return d !== 0 && i !== arr.length - 1;
            }).classed('middle', true);
        }
    }

</script>
<div class="multiple-item" style="width: {100 / columns}%" bind:this={containerElement} bind:clientWidth={width}>
    <div class="multiple-item-header">
        <h3 class="games-year">{games_year === 1964 ? '1964 and 1976' : games_year}</h3>
        <p class="city">{city}</p>
    </div>
    <div class="multiple-chart"  bind:clientHeight={height} data-venue={venue}>
        <svg width={width} height="{height}" id="c-{games_year}">
            {#if chartWidth && chartHeight}
            <rect transform={`translate(${margin.left}, ${margin.top})`} width={chartWidth} height={chartHeight} fill="#ffffff" fill-opacity='0.4' />
            {/if}
            
            <g transform={`translate(${margin.left}, ${margin.top})`}>
                
                    <g class="axis x-axis" class:nolabels={!showAxisLabels} transform={`translate(0, ${chartHeight})`} bind:this={xAxisElement}></g>
                    <g class="axis y-axis" class:nolabels={!showAxisLabels} bind:this={yAxisElement}></g>
                
                {#if linePath} 
                    <path d={linePath} fill="none" stroke="#32699d" class="year-path"stroke-width="1"/>
                {/if}
                {#if trendPath} 
                    <path d={trendPath} fill="none" stroke="#32699d"  stroke-width="2.5"/>
                {/if}
                
            </g>
        </svg>
    </div>      
</div>
<style lang='scss'>
    .multiple-item {
        margin-bottom: 20px;
    }
    .games-year {
        margin-bottom: 0;
        font-size: 14px;
        font-family: "CNN", "CNN Sans Display", "CNN Sans", "Helvetica Neue", Helvetica, Utkal, Arial, sans-serif;

    }
    .city {
        font-size: 12px;
        margin: 0;
        font-weight: 300;
    }
    .country {
        font-size: 12px;
        margin: 0;
        margin-bottom: 10px;
        
        font-weight: 300;
    }
    .multiple-item-header {
       margin-bottom: 10px;
    }
    h3 {
        margin: 0;
        font-size: 12px;
        font-weight: 600;
    }
    .multiple-chart {
        height: 130px;
        @media screen and (max-width: 735px) {
            height: 100px;
        }
    }
    svg {
        overflow:visible;
       
    }
    :global(.axis .tick text) {
        font-family: "CNN", "CNN Sans Display", "CNN Sans", "Helvetica Neue", Helvetica, Utkal, Arial, sans-serif;
        font-size: 12px;
        fill: #8C8C8C;
    }
    :global(.axis .tick line) {
        stroke: #D9D9D9;
    }
    :global(.axis .domain) {
        display: none;
    }
    :global(.axis.nolabels .tick text) {
        display: none;
    }
    :global(.x-axis .tick.first text) {
        text-anchor: start;
    }
    :global(.x-axis .tick.last text) {
        text-anchor: end;
    }
    :global(.y-axis .tick.last text) {
        text-anchor: start;
        transform: translate(5px, -8px);
    }
    :global(.y-axis .tick.zero text) {
        text-anchor: start;
        transform: translate(5px, -8px);
    }
    :global(.y-axis .tick.middle text) {
        display: none;
    }

        .multiple-chart .year-path{
            opacity: 0.5;
        }

    
</style>